import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/shadcn/button';
import { useWarehouseMapStore } from '../../stores/warehouseMapStore';

interface WarehouseAdminToolbarProps {
  baseGrid: { grid_rows: number; grid_cols: number } | undefined;
  syncLayoutMutation: { mutate: () => void; isPending: boolean };
}

export const WarehouseAdminToolbar = ({ baseGrid, syncLayoutMutation }: WarehouseAdminToolbarProps) => {
  const store = useWarehouseMapStore();
  const { t } = useTranslation('warehouseMapModule');

  const newCount = store.localRacks.length;
  const deleteCount = store.deletedRackIds.size;
  const wallCount = store.localWallCells.size;
  const localHasChanges =
    newCount > 0 || deleteCount > 0 || store.localRows !== null || store.localCols !== null || wallCount > 0;

  return (
    <div className="flex gap-2 shrink-0 items-center">
      <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
        {t('view.rowsLabel')}
        <input
          type="number"
          min={1}
          value={store.displayRows ?? baseGrid?.grid_rows ?? ''}
          onChange={(e) => store.setDisplayRows(e.target.value === '' ? null : e.target.value)}
          onBlur={() => {
            if (store.displayRows !== null && store.displayRows !== String(baseGrid?.grid_rows)) {
              const val = parseInt(store.displayRows, 10);
              if (!isNaN(val) && val > 0) store.setLocalRows(val);
            } else {
              store.setLocalRows(null);
            }
          }}
          className="ml-2 w-20 rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </label>

      <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
        {t('view.colsLabel')}
        <input
          type="number"
          min={1}
          value={store.displayCols ?? baseGrid?.grid_cols ?? ''}
          onChange={(e) => store.setDisplayCols(e.target.value === '' ? null : e.target.value)}
          onBlur={() => {
            if (store.displayCols !== null && store.displayCols !== String(baseGrid?.grid_cols)) {
              const val = parseInt(store.displayCols, 10);
              if (!isNaN(val) && val > 0) store.setLocalCols(val);
            } else {
              store.setLocalCols(null);
            }
          }}
          className="ml-2 w-20 rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </label>

      {localHasChanges && (
        <>
          <Button
            variant="ghost"
            onClick={() => {
              store.setLocalRows(null);
              store.setLocalCols(null);
              if (baseGrid) {
                store.setDisplayRows(String(baseGrid.grid_rows));
                store.setDisplayCols(String(baseGrid.grid_cols));
              }
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            {t('view.resetDimensions')}
          </Button>
          <Button
            onClick={() => syncLayoutMutation.mutate()}
            disabled={syncLayoutMutation.isPending}
            className="bg-primary text-white"
          >
            {syncLayoutMutation.isPending ? t('view.saving') : t('view.save')}
          </Button>
        </>
      )}

      {localHasChanges && (
        <Button variant="ghost" onClick={store.resetAll} className="text-muted-foreground hover:text-foreground">
          {t('view.cancelAll')}
        </Button>
      )}
    </div>
  );
};
